import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Receipt, Search, Share2, Copy } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { FullLoader, EmptyState } from "../components/common";
import { TxnRow } from "../components/TxnRow";

const REACTIONS = ["👍", "❤️", "😂", "🎉", "🔥", "🙏", "😮", "💸"];

const FILTERS = [
  { id: "", label: "All", match: () => true },
  { id: "send", label: "Sent", match: (t) => t.type === "send" },
  { id: "receive", label: "Received", match: (t) => t.type === "receive" },
  { id: "savings", label: "Savings", match: (t) => t.type === "savings_deposit" || t.type === "savings_withdraw" },
  { id: "deposit", label: "Added", match: (t) => t.type === "deposit" },
  { id: "refund", label: "Refunds", match: (t) => t.type === "refund" },
];

function receiptText(t) {
  const lines = [
    "JACK CASH — RECEIPT",
    "────────────────────",
    `${t.amount >= 0 ? "Received" : "Sent"}: ${t.amount >= 0 ? "+" : "-"}${t.amount_display}`,
    `To/From: ${t.counterparty_name || "—"}${t.counterparty_tag ? ` (${t.counterparty_tag})` : ""}`,
    `Status: ${t.status}`,
    t.description ? `Note: ${t.description}` : null,
    `Reference: ${t.provider_reference || t.transaction_id}`,
    `Date: ${new Date(t.timestamp).toLocaleString()}`,
    "────────────────────",
    "Sent with Jack Cash",
  ].filter(Boolean);
  return lines.join("\n");
}

export default function Activity() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/transactions?limit=200");
        setAll(data);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const txns = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0];
    const q = query.trim().toLowerCase();
    return all.filter((t) => f.match(t) && (!q ||
      (t.counterparty_name || "").toLowerCase().includes(q) ||
      (t.description || "").toLowerCase().includes(q) ||
      (t.counterparty_tag || "").toLowerCase().includes(q)));
  }, [all, filter, query]);

  const shareReceipt = async (t) => {
    const text = receiptText(t);
    try {
      if (navigator.share) await navigator.share({ title: "Jack Cash Receipt", text });
      else { await navigator.clipboard.writeText(text); toast.success("Receipt copied"); }
    } catch {}
  };
  const copyReceipt = async (t) => {
    await navigator.clipboard.writeText(receiptText(t));
    toast.success("Receipt copied");
  };

  const react = async (emoji) => {
    const next = selected.reaction === emoji ? null : emoji;
    try {
      await api.post(`/transactions/${selected.transaction_id}/reaction`, { emoji: next });
      setSelected((s) => ({ ...s, reaction: next }));
      setAll((list) => list.map((t) => t.transaction_id === selected.transaction_id ? { ...t, reaction: next } : t));
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="animate-fade-up">
      <h1 className="font-display text-2xl font-semibold tracking-tight mb-4">Activity</h1>

      <div className="relative mb-3">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="activity-search" placeholder="Search name, note or Cash Tag"
          value={query} onChange={(e) => setQuery(e.target.value)}
          className="w-full h-12 pl-11 pr-4 rounded-2xl bg-secondary border border-transparent focus:border-primary focus:bg-card outline-none text-sm" />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-3">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`filter-${f.id || "all"}`} onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${filter === f.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <FullLoader /> : txns.length === 0 ? (
        <EmptyState icon={Receipt} title="No transactions found"
          body={query || filter ? "Try a different search or filter." : "Your transaction history will appear here."} />
      ) : (
        <div className="divide-y divide-border">
          {txns.map((t) => <TxnRow key={t.transaction_id} t={t} onClick={() => setSelected(t)} />)}
        </div>
      )}

      {selected && (
        <div data-testid="txn-detail-modal" className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={() => setSelected(null)}>
          <div className="w-full max-w-md bg-card rounded-t-3xl p-6 animate-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-border mx-auto mb-5" />
            <div className="text-center">
              <div className="text-xs tracking-wide uppercase text-muted-foreground font-semibold capitalize">{selected.type.replace("_", " ")} · {selected.status}</div>
              <div className={`amount-figure text-4xl mt-2 ${selected.amount >= 0 ? "text-success" : ""}`}>
                {selected.amount >= 0 ? "+" : "-"}{selected.amount_display}
              </div>
            </div>
            <div className="mt-6 space-y-3 text-sm">
              {[
                ["Counterparty", selected.counterparty_name],
                ["Cash Tag", selected.counterparty_tag],
                ["Description", selected.description],
                ["Reference", selected.provider_reference || selected.transaction_id],
                ["Date", new Date(selected.timestamp).toLocaleString()],
                ...(selected.failure_reason ? [["Failure reason", selected.failure_reason]] : []),
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-medium text-right break-all">{v || "—"}</span>
                </div>
              ))}
            </div>
            <div className="mt-6" data-testid="reaction-picker">
              <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-2">React</p>
              <div className="flex flex-wrap gap-2">
                {REACTIONS.map((emoji) => (
                  <button key={emoji} data-testid={`reaction-${emoji}`} onClick={() => react(emoji)}
                    className={`w-11 h-11 rounded-full grid place-items-center text-xl transition-all ${selected.reaction === emoji ? "bg-primary/15 ring-2 ring-primary scale-105" : "bg-secondary hover:bg-secondary/70"}`}>
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <button data-testid="copy-receipt-btn" onClick={() => copyReceipt(selected)}
                className="h-12 rounded-2xl bg-secondary font-semibold flex items-center justify-center gap-2"><Copy size={17} /> Copy</button>
              <button data-testid="share-receipt-btn" onClick={() => shareReceipt(selected)}
                className="h-12 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2"><Share2 size={17} /> Share receipt</button>
            </div>
            <button data-testid="close-txn-detail" onClick={() => setSelected(null)}
              className="w-full h-12 rounded-2xl bg-transparent text-muted-foreground font-semibold mt-2">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
